import * as React from "react";
import styled from "@emotion/styled";
import { Button, FadeIn, H2, IconChevron, Text } from "atoms";
import gem from "assets/images/roadmap_game.svg";
import { Thumbnail } from "features/images";
import { themePrototype } from "styles/themePrototype";
import { formatDate } from "utils/date";
import uniqueId from "lodash/uniqueId";
import { RaffleItem } from "../types";

type MoreItemsProps = {
  items: RaffleItem[];
  label: string;
};

function MoreItems({ items, label }: MoreItemsProps) {
  const [isExpanded, setIsExpanded] = React.useState(false);
  const toggle = () => setIsExpanded(!isExpanded);

  const [componentId] = React.useState(uniqueId());

  return (
    <MoreItemsMain>
      {items.length > 0 && (
        <>
          <MoreItemsGrid role="list" isExpanded={isExpanded}>
            <TitleItem>
              <H2>{label}</H2>
              {items.length > 3 && (
                <ToggleButton
                  type="button"
                  variant="blank"
                  size="sm"
                  onClick={toggle}
                  isExpanded={isExpanded}
                  aria-controls={componentId}
                  aria-label={isExpanded ? "Collapse" : "Show all"}
                  aria-expanded={isExpanded ? "true" : "false"}
                >
                  {isExpanded ? "Collapse" : "Show all"}
                </ToggleButton>
              )}
            </TitleItem>

            {items?.map(
              ({
                id,
                image,
                title,
                cost,
                quantity,
                startDate,
                endDate,
              }: RaffleItem) => (
                <Item isExpanded={isExpanded} key={id}>
                  <ItemFigure>
                    <ItemImg src={image} alt="" small />
                  </ItemFigure>
                  <ItemBody>
                    <ItemTitle size="xs" bold>
                      {title}
                    </ItemTitle>
                    <ItemInfo>
                      <Text size="xxs">
                        <img src={gem} alt="" width="16" height="16" />
                        {cost.toLocaleString()}
                      </Text>
                      <Text size="xxs">{quantity} available</Text>
                    </ItemInfo>
                    <ItemDate size="xxs" bold>
                      Starts{" "}
                      <span>
                        {formatDate(startDate.toISOString(), {
                          month: "short",
                          day: "numeric",
                          hour: "numeric",
                          timeZoneName: "short",
                        })}
                      </span>
                      {/* {" "}
                      {(endDate.getTime() - startDate.getTime()) / (1000 * 3600 * 24)}&nbsp;days */}
                      {/* <br />
                      to{" "}
                      {formatDate(endDate.toISOString(), {
                        month: "short",
                        day: "numeric",
                        hour: "numeric",
                        timeZoneName: "short",
                      })} */}
                    </ItemDate>
                  </ItemBody>
                </Item>
              )
            )}
          </MoreItemsGrid>
          {items.length > 3 && (
            <SToggleButton
              type="button"
              variant="blank"
              size="sm"
              onClick={toggle}
              isExpanded={isExpanded}
              aria-controls={componentId}
              aria-label={isExpanded ? "Collapse" : "Show all"}
              aria-expanded={isExpanded ? "true" : "false"}
            >
              <IconChevron />
              &nbsp;{isExpanded ? "Collapse" : "Show all"}
            </SToggleButton>
          )}
        </>
      )}
    </MoreItemsMain>
  );
}
const MoreItemsMain = styled.section({
  margin: "5rem 0",
});
const MoreItemsGrid = styled.div<{ isExpanded?: boolean }>(
  ({ isExpanded, theme }) => ({
    display: "grid",
    gridTemplateColumns: "repeat(2, 1fr)",
    gap: "1rem",
    paddingBottom: "1rem",
    "@media (min-width: 800px)": {
      gridTemplateColumns: "repeat(4, 1fr)",
    },
  })
);

const Item = styled(FadeIn, {
  shouldForwardProp: (prop) => prop !== "isExpanded",
})<{ isExpanded?: boolean }>(({ isExpanded, theme }) => ({
  flex: "1 1 40%",
  overflow: "hidden",
  backgroundColor: "white",
  borderRadius: themePrototype.borderRadius.md,
  boxShadow: theme.boxShadow.light,
  paddingBottom: "1em",
  "&>*": {
    margin: "0 !important",
  },
  "&:nth-of-type(n+4)": {
    display: "none",
    ...(isExpanded && {
      display: "block",
    }),
  },
  "@media (min-width: 600px)": {
    "&:nth-of-type(n+4)": {
      display: "block",
    },
    "&:nth-of-type(n+5)": {
      display: "none",
      ...(isExpanded && {
        display: "block",
      }),
    },
  },
  "@media (min-width: 800px)": {},
})).withComponent("li");
const TitleItem = styled(Item)(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  justifyItems: "center",
  alignItems: "center",
  textAlign: "center",
  justifyContent: "center",
  backgroundImage: theme.gradients.linear,
  padding: "2em 1em",
  gap: "1rem",
  gridArea: "1 / 1 / 1 / 3",
  "@media (min-width: 600px)": {
    gridArea: "auto",
  },
}));
const ItemFigure = styled.figure({
  width: "100%",
  aspectRatio: "1 / 1",
  padding: "1em",
  maxHeight: "150px",
  "@media (min-width: 800px)": {
    flex: "1",
    maxHeight: "20vw",
  },
});
const ItemBody = styled.div(({ theme }) => ({
  alignItems: "center",
  display: "flex",
  flexDirection: "column",
  gap: 8,
  justifyItems: "center",
  textAlign: "center",
}));
const ItemImg = styled(Thumbnail)({
  display: "block",
  objectFit: "contain",
  objectPosition: "center",
  width: "100%",
  height: "100%",
});
const ItemTitle = styled(Text)({
  margin: 0,
  padding: "0 .5rem",
  lineHeight: "1.2",
});
const ItemInfo = styled.div({
  display: "inline-flex",
  flexWrap: "wrap",
  alignItems: "center",
  justifyContent: "center",
  gap: "3px 8px",
  padding: "0 .5rem",
  p: {
    display: "inline-flex",
    alignItems: "center",
    gap: 2,
    margin: 0,
  },
});
const ItemDate = styled(Text)({
  lineHeight: "1.2",
  margin: 0,
  padding: "0 .5rem",
  span: {
    whiteSpace: "nowrap",
  },
});

const ToggleButton = styled(Button, {
  shouldForwardProp: (prop) => prop !== "isExpanded",
})<{ isExpanded: boolean }>(({ isExpanded, theme }) => ({
  "&:hover, &:focus-visible": {
    color: theme.colors.black,
    textDecoration: "underline",
  },
  "li &": {
    display: "none",
    "@media (min-width: 800px)": {
      display: "block",
    },
  },
  ...(!isExpanded && {
    svg: {
      transform: "scale(1, -1)",
    },
  }),
}));
const SToggleButton = styled(ToggleButton, {
  shouldForwardProp: (prop) => prop !== "isExpanded",
})<{ isExpanded: boolean }>(({ isExpanded, theme }) => ({
  display: "block",
  width: "100%",
  padding: "4px 1em",
  backgroundColor: theme.colors.gray10,
  borderRadius: theme.borderRadius.sm,
  textDecoration: "none",
  fontWeight: "bold",
  "&:hover, &:focus-visible": {
    textDecoration: "none",
  },
  "@media (min-width: 800px)": {
    display: "none",
    ...(isExpanded && {
      display: "block",
    }),
  },
}));
export { MoreItems };
